import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { IMAGES } from "../../data";

const EASE = [0.33, 1, 0.68, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex items-center overflow-hidden bg-[#EAF7FC] pt-28 md:pt-32 pb-16 md:pb-20 min-h-[88vh]"
    >
      <div className="pointer-events-none absolute -top-24 -left-24 h-[360px] w-[360px] rounded-full bg-[#54BCE6]/25 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-1/3 h-[280px] w-[280px] rounded-full bg-[#133955]/10 blur-3xl" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur px-4 py-2 mb-6 border border-[rgba(19,57,85,0.08)]"
          >
            <MapPin size={15} className="text-[#54BCE6]" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#133955]">
              General Contractors & Home Pros · SC Lowcountry
            </span>
          </motion.div>

          <h1 data-testid="hero-heading" className="font-display text-[#133955] font-extrabold tracking-tighter leading-[0.95] text-5xl md:text-6xl lg:text-7xl">
            <span className="block overflow-hidden pb-[0.18em] -mb-[0.12em]">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}>
                {"Connect With a Trusted "}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-[0.18em] -mb-[0.12em]">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.8, ease: EASE, delay: 0.27 }}>
                {"General Contractor "}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-[0.18em] mt-2">
              <motion.span className="block text-3xl md:text-4xl lg:text-5xl font-bold text-[#54BCE6]" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.8, ease: EASE, delay: 0.42 }}>
                or Home Service Professional.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-[#173042]/80"
          >
            Tell us about your project once. Home Pro Connect connects homeowners in Summerville,
            Charleston, North Charleston, and across the Lowcountry with a vetted, licensed, and
            insured{" "}
            <a href="/general-contractor" data-testid="hero-gc-link" className="underline decoration-[#54BCE6]/60 underline-offset-4 hover:text-[#133955] transition-colors">general contractor</a>
            {" "}for kitchen and bath remodels, additions, and whole-home renovations, and with
            local{" "}
            <a href="/home-services" data-testid="hero-hs-link" className="underline decoration-[#54BCE6]/60 underline-offset-4 hover:text-[#133955] transition-colors">home service professionals</a>
            {" "}for HVAC, plumbing, roofing, flooring, and more.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.72 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="/general-contractor"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#133955] text-white px-8 py-4 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#133955] focus-visible:ring-offset-2"
            >
              Find a General Contractor
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="/home-services"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center rounded-full border-2 border-[#133955] text-[#133955] px-8 py-4 font-bold hover:bg-[#133955] hover:text-white transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54BCE6] focus-visible:ring-offset-2"
            >
              Find a Home Service Pro
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.84 }}
            data-testid="hero-trust-line"
            className="mt-4 text-xs md:text-sm text-[#173042]/55"
          >
            Free for homeowners · Pros vetted for licensing, insurance & 4.5+ Google rating
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.3 }}
          className="lg:col-span-6 relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(19,57,85,0.25)] aspect-[4/5] md:aspect-[5/5]">
            <motion.img
              src={IMAGES.hero}
              alt="General contractor reviewing renovation plans in a Lowcountry, SC home"
              loading="eager"
              fetchPriority="high"
              width="1000"
              height="1250"
              style={{ y, scale }}
              className="absolute inset-0 w-full h-[115%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#133955]/55 via-transparent to-transparent" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="absolute -bottom-6 -left-4 md:-left-8 max-w-[260px] bg-white rounded-2xl px-6 py-5 shadow-[0_16px_48px_rgba(19,57,85,0.16)] border border-[#EAF7FC]"
          >
            <p className="font-display text-xl font-extrabold text-[#133955] leading-tight">
              Start With One Simple Request
            </p>
            <p className="text-xs font-semibold text-[#173042]/70 mt-2">
              Share your project once. Free for homeowners. We'll help guide the connection.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
